/**
 * Call tones for the softphone, synthesised with WebAudio so the bundle
 * carries no audio assets:
 *
 * - `startRingtone()`  - inbound call ringing here (WhatsApp-ish rising
 *   three-note phrase, repeated).
 * - `startRingback()`  - outbound call ringing at the customer's end (the
 *   Indian network ringback: two 0.4s bursts, then a long gap).
 * - `playEndTone()`    - the call is over (two short low blips).
 *
 * Browsers block audio until the page has had a user gesture. Dialling is a
 * click, so ringback always plays; an inbound ring on a page nobody has
 * touched may stay silent, and the visual ring is what the agent sees. On
 * phones `installCallAudioUnlock` closes that gap: any tap in the app
 * unlocks the audio, so a ring that comes later is heard.
 */

import { isPhoneDevice } from '@/lib/device';

let ctx: AudioContext | null = null;

/** Inbound ring loudness. A phone is held away / in a pocket - ring louder there. */
const RING_GAIN_DESKTOP = 0.1;
const RING_GAIN_PHONE = 0.3;
export const ringGain = (): number => (isPhoneDevice() ? RING_GAIN_PHONE : RING_GAIN_DESKTOP);

/** The one unlocked AudioContext - shared with the message chime. */
export const audioContext = (): AudioContext | null => {
  try {
    ctx ??= new AudioContext();
    // 'suspended' (no tap yet / backgrounded) or Safari's 'interrupted' (after
    // a phone call, Siri...): try to start it again.
    if (ctx.state !== 'running' && ctx.state !== 'closed') {
      void ctx.resume().catch(() => undefined);
    }
    return ctx;
  } catch {
    return null;
  }
};

/** One shaped sine burst. `at` and `duration` are seconds. */
const beep = (
  audio: AudioContext,
  { at, duration, freq, gain }: { at: number; duration: number; freq: number; gain: number },
): void => {
  const osc = audio.createOscillator();
  const env = audio.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  // Ramps rather than steps: an instant start/stop on a sine clicks.
  env.gain.setValueAtTime(0.0001, at);
  env.gain.exponentialRampToValueAtTime(gain, at + 0.02);
  env.gain.setValueAtTime(gain, at + duration - 0.05);
  env.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  osc.connect(env).connect(audio.destination);
  osc.start(at);
  osc.stop(at + duration + 0.02);
};

/** Play `pattern` now and every `everyMs` until the returned stop() is called. */
const loop = (
  everyMs: number,
  pattern: (audio: AudioContext, now: number) => void,
): (() => void) => {
  const play = () => {
    const audio = audioContext();
    if (audio) pattern(audio, audio.currentTime);
  };
  play();
  const timer = window.setInterval(play, everyMs);
  return () => window.clearInterval(timer);
};

/** Inbound ring: three rising notes, twice, then a gap - repeats every 3s. */
export const startRingtone = (): (() => void) => {
  const gain = ringGain();
  return loop(3_000, (audio, now) => {
    const notes = [784, 988, 1175]; // G5, B5, D6
    notes.forEach((freq, i) => {
      beep(audio, { at: now + i * 0.18, duration: 0.16, freq, gain });
      beep(audio, { at: now + 0.75 + i * 0.18, duration: 0.16, freq, gain });
    });
  });
};

/** Outbound ringback: 0.4s on, 0.2s off, 0.4s on, then silence until the next cycle. */
export const startRingback = (): (() => void) =>
  loop(4_000, (audio, now) => {
    beep(audio, { at: now, duration: 0.4, freq: 425, gain: 0.05 });
    beep(audio, { at: now + 0.6, duration: 0.4, freq: 425, gain: 0.05 });
  });

/** Call over: two short low blips. */
export const playEndTone = (): void => {
  const audio = audioContext();
  if (!audio) return;
  const now = audio.currentTime;
  beep(audio, { at: now, duration: 0.14, freq: 420, gain: 0.07 });
  beep(audio, { at: now + 0.2, duration: 0.18, freq: 330, gain: 0.07 });
};

/** Events a browser counts as a user gesture for starting audio (not touchstart). */
const UNLOCK_EVENTS = ['touchend', 'click', 'keydown'] as const;

/**
 * Unlock call audio on the first tap anywhere, and again after the phone
 * suspended it (app sent to the background), so an inbound ring isn't
 * silent just because nobody had touched the page yet. Plays one silent
 * sample - all iOS needs to treat the audio as started by the user. Makes no
 * sound and asks for nothing. Returns the cleanup.
 */
export const installCallAudioUnlock = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  const unlock = (): void => {
    if (ctx?.state === 'running') return;
    const audio = audioContext();
    if (!audio) return;
    try {
      const source = audio.createBufferSource();
      source.buffer = audio.createBuffer(1, 1, audio.sampleRate);
      source.connect(audio.destination);
      source.start(0);
    } catch {
      // audioContext() already called resume(); the silent sample is a nicety.
    }
  };
  for (const type of UNLOCK_EVENTS) {
    window.addEventListener(type, unlock, { capture: true, passive: true });
  }
  return () => {
    for (const type of UNLOCK_EVENTS) {
      window.removeEventListener(type, unlock, { capture: true });
    }
  };
};
